import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Form, message } from 'antd'
import dayjs from 'dayjs'
import { debounce } from '../utils/debounce'
import { usePharmacyNoteConfig } from './usePharmacyNoteConfig'

export const useTreatmentForm = ({ 
    onCancel, 
    onOk,
    apiClasses,
    caseDetails,
    currentPharmacy,
    currentPharmacyId,
    selectedPrescriptionId,
    selectedPrescription,
    templates,
    templatesLoading,
    searchTemplates,
    dispenseUnits,
    getDispenseUnitById,
    mode,
    apiType
}) => {
    const [form] = Form.useForm()
    const [medicationSearchState, setMedicationSearchState] = useState({
        loading: false,
        matches: [],
        selectedMedication: null,
        selectedStrength: null
    })
    const [showPharmacyNotes, setShowPharmacyNotes] = useState(false)

    const effectivePharmacyId = currentPharmacyId || caseDetails?.pharmacyId
    const { pharmacyNoteConfig, fetchConfig: fetchPharmacyNoteConfig } = usePharmacyNoteConfig(effectivePharmacyId, apiClasses)

    const abortController = useRef(null)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [existingPrescription, setExistingPrescription] = useState(null)


    useEffect(() => {
        if (selectedPrescription && selectedPrescriptionId) {
            setExistingPrescription(selectedPrescription)
            
            const formData = {
                name: selectedPrescription.drugName || selectedPrescription.name,
                strength: selectedPrescription.strength,
                quantity: selectedPrescription.quantity,
                refills: selectedPrescription.refills,
                daysSupply: selectedPrescription.daysSupply,
                startDate: selectedPrescription.startDate ? dayjs(selectedPrescription.startDate) : dayjs(),
                pharmacyNotes: selectedPrescription.pharmacyNotes,
                directions: selectedPrescription.instructions || selectedPrescription.directions,
                quantityUnits: selectedPrescription.quantityUnits,
                doseForm: selectedPrescription.doseForm,
                noSubstitutions: selectedPrescription.noSubstitutions || false,
                isUrgent: selectedPrescription.isUrgent || false,
                providePharmacyNotes: !!selectedPrescription.pharmacyNotes,
                valid: selectedPrescription.valid || false
            }

            if (selectedPrescription.dispenseUnitId) {
                formData.dispenseUnitId = parseInt(selectedPrescription.dispenseUnitId)
            }
            
            form.setFieldsValue(formData)
            
            if (selectedPrescription.drugName || selectedPrescription.name) {
                setMedicationSearchState(prev => ({
                    ...prev,
                    selectedMedication: {
                        drugName: selectedPrescription.drugName || selectedPrescription.name,
                        strength: selectedPrescription.strength,
                        dispensableDrugId: selectedPrescription.dispensableDrugId,
                        quantityUnits: selectedPrescription.quantityUnits,
                        doseForm: selectedPrescription.doseForm,
                        schedule: selectedPrescription.schedule,
                        ndc: selectedPrescription.ndc
                    },
                    selectedStrength: {
                        strength: selectedPrescription.strength,
                        dispensableDrugId: selectedPrescription.dispensableDrugId,
                        quantityUnits: selectedPrescription.quantityUnits,
                        doseForm: selectedPrescription.doseForm,
                        schedule: selectedPrescription.schedule,
                        ndc: selectedPrescription.ndc
                    }
                }))
            }
            
            if (selectedPrescription.pharmacyNotes) {
                setShowPharmacyNotes(true)
            }
        } else {
            setExistingPrescription(null)
            if (mode === 'edit') {
                form.resetFields()
                setMedicationSearchState({
                    loading: false,
                    matches: [],
                    selectedMedication: null,
                    selectedStrength: null
                })
                setShowPharmacyNotes(false)
            }
        }
    }, [selectedPrescription, selectedPrescriptionId, form, dispenseUnits, mode])


    const searchMedications = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 2) {
            setMedicationSearchState(prev => ({ ...prev, matches: [], loading: false }))
            return
        }

        if (!apiClasses?.PrescriptionsAPI) {
            return
        }

        if (abortController.current) {
            abortController.current.abort()
        }
        abortController.current = new AbortController()

        setMedicationSearchState(prev => ({ ...prev, loading: true, matches: [] }))
        
        try {
            const abbreviatedSearch = searchTerm.split(' ')[0]
            const response = await apiClasses.PrescriptionsAPI.searchMedications(abbreviatedSearch, abortController.current.signal)
            
            if (abortController.current.signal.aborted) {
                return
            }
        
            const medications = response.data?.medications || []
            
            setMedicationSearchState(prev => ({ 
                ...prev, 
                loading: false, 
                matches: medications
            }))
        } catch (error) {
            if (error.name === 'AbortError' || abortController.current?.signal.aborted) {
                return
            }
            
            setMedicationSearchState(prev => ({ 
                ...prev, 
                loading: false, 
                matches: []
            }))
        }
    }, [apiClasses])

    const debouncedSearchMedications = useMemo(
        () => debounce(searchMedications, 300),
        [searchMedications]
    )

    const selectMedication = useCallback((medicationName) => {
        const medication = medicationSearchState.matches.find(med => med.name === medicationName)
        if (medication) {
            setMedicationSearchState(prev => ({
                ...prev,
                selectedMedication: medication,
                selectedStrength: null
            }))
            
            const dispenseUnit = getDispenseUnitById(medication.dispenseUnitId)
            const isCompound = !!medication.prescribingDefaults
            
            const newFormValues = {
                name: medication.name,
                strength: null,
                dispenseUnitId: medication.dispenseUnitId,
                quantityUnits: medication.quantityUnits || dispenseUnit?.Name || 'Tablet',
                directions: '', 
                pharmacyNotes: '',
                quantity: null,
                daysSupply: null,
                refills: null,
            }
            
            if (isCompound) {
                newFormValues.strength = 'Compound'
                Object.assign(newFormValues, medication.prescribingDefaults || {})
            }
            
            form.setFieldsValue(newFormValues)
            
            searchTemplates({
                categoryId: caseDetails?.category?._id,
                drugName: medication.name,
                stateId: caseDetails?.stateId
            })
        }
    }, [form, medicationSearchState.matches, getDispenseUnitById, searchTemplates, caseDetails])

    const selectStrength = useCallback((strengthValue) => {
        const { selectedMedication } = medicationSearchState
        if (selectedMedication) {
            const availableStrengths = selectedMedication.strengths || [selectedMedication]
            const selectedStrength = availableStrengths.find(medStrength => medStrength.strength === strengthValue)
            
            if (selectedStrength) {
                setMedicationSearchState(prev => ({
                    ...prev,
                    selectedStrength
                }))
                
                const newFormValues = {
                    ...selectedStrength,
                    quantityUnits: selectedStrength.doseForm || selectedStrength.dispenseUnit || 'Compound',
                    directions: '', 
                    pharmacyNotes: ''
                }
                
                form.setFieldsValue(newFormValues)
                
                searchTemplates({
                    categoryId: caseDetails?.category?._id,
                    drugName: selectedMedication.name,
                    strength: strengthValue,
                    stateId: caseDetails?.stateId
                })
            }
        }
    }, [medicationSearchState.selectedMedication, form, searchTemplates, caseDetails])

    const handleSelectTemplate = useCallback((template) => {
        setSelectedTemplate(template)
        if (template) {
            const { medication, directions, pharmacyNotes, DAW } = template
            const { drugName, strength, pillCount, refills, daysSupply, dispenseUnitId } = medication

            // Load the note config for whichever pharmacy this template targets.
            // If the template has no pharmacy, fall back to the case's own pharmacy
            // (passing null/undefined resets to the default config).
            fetchPharmacyNoteConfig(template.pharmacyId || effectivePharmacyId)
            
            const dispenseUnit = getDispenseUnitById(dispenseUnitId)

            const templateStrength = {
                strength: strength,
                dispensableDrugId: medication.dispensableDrugId,
                dispenseUnitId: dispenseUnitId,
                quantityUnits: dispenseUnit?.Name,
                doseForm: medication.doseForm || dispenseUnit?.Name,
                schedule: medication.schedule || 0,
                ndc: medication.ndc,
                DAW: medication.DAW
            }
            
            const templateMedication = {
                name: drugName,
                dispensableDrugId: medication.dispensableDrugId,
                dispenseUnitId: dispenseUnitId,
                quantityUnits: dispenseUnit?.Name,
                doseForm: medication.doseForm || dispenseUnit?.Name,
                schedule: medication.schedule || 0,
                ndc: medication.ndc,
                strengths: medication.strengths || [templateStrength],
                DAW: medication.DAW
            }
            
            setMedicationSearchState(prev => ({
                ...prev,
                selectedMedication: templateMedication,
                selectedStrength: templateStrength
            }))
            
            form.setFieldsValue({
                name: drugName,
                strength: strength,
                directions: directions,
                quantity: pillCount,
                refills: refills,
                daysSupply: daysSupply,
                dispenseUnitId: dispenseUnitId,
                quantityUnits: dispenseUnit?.Name || 'Tablet',
                pharmacyNotes: pharmacyNotes,
                noSubstitutions: !!DAW,
                providePharmacyNotes: !!pharmacyNotes,
                DAW: !!DAW
            })
            
            setShowPharmacyNotes(!!pharmacyNotes)
        }
    }, [form, getDispenseUnitById, fetchPharmacyNoteConfig, effectivePharmacyId])

    const handleFinish = useCallback((values) => {
        const { selectedMedication, selectedStrength } = medicationSearchState
        
        if (!selectedMedication) {
            message.error('Please select a medication from the search results')
            return
        }
        
        if (!selectedStrength && !selectedMedication.dispensableDrugId) {
            message.error('Please select a strength for the medication')
            return
        }
        
        const templatePharmacy = selectedTemplate?.pharmacy
        const templatePharmacyId = selectedTemplate?.pharmacyId
        const existingPharmacy = existingPrescription?.pharmacy
        const existingPharmacyId = existingPrescription?.pharmacyId
        
        const prescription = {
            ...values,
            startDate: values.startDate ? values.startDate.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
            pharmacy: templatePharmacy || existingPharmacy || currentPharmacy || caseDetails?.pharmacy, 
            pharmacyId: templatePharmacyId || existingPharmacyId || currentPharmacyId || caseDetails?.pharmacyId, 
            caseId: caseDetails?._id,
            patientId: caseDetails?.patient?._id,
            providerId: caseDetails?.assignedPhysician,
            valid: true, 
            mismatch: false, 
            _id: existingPrescription?._id || Date.now(), 
            drugName: values.name || selectedMedication?.name,
            name: values.name || selectedMedication?.name,
            strength: values.strength || selectedStrength?.strength,
            ingredients: values.ingredients || null,
            dispensableDrugId: selectedMedication?.dispensableDrugId || selectedStrength?.dispensableDrugId,
            quantityUnits: values.quantityUnits || selectedMedication?.quantityUnits || selectedStrength?.quantityUnits || 'Tablet',
            doseForm: values.doseForm || selectedMedication?.doseForm || selectedStrength?.doseForm || 'Tablet',
            schedule: selectedMedication?.schedule || selectedStrength?.schedule || 0,
            ndc: selectedMedication?.ndc || selectedStrength?.ndc,
            template: null,
            templateId: null,
            templateName: null,
            templateData: null,
            templateTitle: null,
            templateAlternatives: null,
            title: null
        }

        if (onOk) {
            onOk(prescription)
        }
        
        form.resetFields()
        setSelectedTemplate(null) 
        setMedicationSearchState({
            loading: false,
            matches: [],
            selectedMedication: null,
            selectedStrength: null
        })
        setShowPharmacyNotes(false)
    }, [form, caseDetails, currentPharmacy, currentPharmacyId, onOk, medicationSearchState, existingPrescription])

    const handleCancel = useCallback(() => {
        if (onCancel) {
            onCancel()
        }
        
        form.resetFields()
        setMedicationSearchState({
            loading: false,
            matches: [],
            selectedMedication: null,
            selectedStrength: null
        })
        setShowPharmacyNotes(false)
    }, [form, onCancel])

    useEffect(() => {
        if (caseDetails?.category?._id && caseDetails?.stateId) {
            searchTemplates({
                categoryId: caseDetails.category._id,
                stateId: caseDetails.stateId,
                _t: new Date().getTime(),
            })
        }
    }, [caseDetails?.category?._id, caseDetails?.stateId, apiType])

    useEffect(() => {
        if (currentPharmacy || caseDetails?.pharmacy) {
            form.setFieldsValue({
                pharmacy: currentPharmacy || caseDetails.pharmacy,
                startDate: dayjs()
            })
        }
    }, [caseDetails, currentPharmacy, form])

    useEffect(() => {
        return () => {
            if (abortController.current) {
                abortController.current.abort()
            }
        }
    }, [])

    return {
        form,
        loading: templatesLoading,
        medicationSearchState,
        showPharmacyNotes,
        setShowPharmacyNotes,
        pharmacyNoteConfig,
        searchMedications: debouncedSearchMedications,
        selectMedication,
        selectStrength,
        handleFinish,
        handleCancel,
        dispenseUnits,
        templates,
        selectedTemplate,
        handleSelectTemplate,
        templatesLoading
    }
}
